import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import { formatPhone } from "@/lib/format";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import { Building2, Save, X, Eye, Image as ImageIcon } from "lucide-react";

const EMPTY = {
  display_name: "",
  tagline: "",
  concierge_email: "",
  phone: "",
  address: "",
  contact_name: "",
  primary_color: "#0B1733",
  accent_color: "#1D4ED8",
  logo_url: "",
};

export default function AdminClinics() {
  const [clinics, setClinics] = useState([]);
  const [editing, setEditing] = useState(null); // user.id
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/clinics-list");
      setClinics(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startEdit = async (c) => {
    setEditing(c.id);
    try {
      const r = await api.get(`/admin/clinics/${c.id}/branding`);
      setForm({ ...EMPTY, ...(r.data || {}) });
    } catch (e) {
      toast.error(formatApiError(e));
      setForm({ ...EMPTY, display_name: c.clinic_name || "" });
    }
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!editing) return;
    try {
      const payload = { ...form };
      for (const k of Object.keys(payload)) if (payload[k] === "") delete payload[k];
      await api.patch(`/admin/clinics/${editing}/branding`, payload);
      toast.success("Branding override saved.");
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const previewSample = async (cid) => {
    try {
      // Use any active landing_page asset.
      const r = await api.get("/marketing-assets");
      const candidate =
        (r.data || []).find((a) => a.template_kind === "landing_page" && a.status === "active") ||
        (r.data || []).find((a) => a.status === "active");
      if (!candidate) {
        toast.error("No active marketing assets to preview.");
        return;
      }
      // Admin preview uses default branding — for clinic-specific preview we'd need to impersonate.
      // For now, open the admin preview endpoint.
      const pdf = await api.get(`/marketing-assets/${candidate.id}/preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Network"
      title="Clinics & branding"
      intro="Override any clinic's branding (logo, colors, contact) when needed. Co-branded marketing PDFs use these values automatically."
    >
      {loading ? (
        <div className="text-sm text-slate-500">Loading clinics…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1.6fr] gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="admin-clinics-list">
            {clinics.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-slate-500">No clinics provisioned yet.</div>
            )}
            {clinics.map((c) => {
              const branding = c.branding || {};
              const isActive = editing === c.id;
              const logo = branding.logo_url;
              const display = branding.display_name || c.clinic_name || c.name || c.email;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`admin-clinic-row-${c.id}`}
                  onClick={() => startEdit(c)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 ${
                    isActive ? "bg-blue-50/60" : ""
                  }`}
                >
                  <span className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                    {logo ? (
                      <img src={resolveAssetUrl(logo)} alt={display} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-4 h-4 text-slate-700" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{display}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                  </div>
                  {branding.updated_at ? (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">
                      Branded
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                      Default
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div>
            {editing ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                      Branding override
                    </div>
                    <div className="font-display text-base font-semibold text-slate-900 mt-0.5">
                      {clinics.find((c) => c.id === editing)?.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => previewSample(editing)}
                      className="text-xs font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> Preview default render
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Display name">
                    <input
                      data-testid="admin-clinic-display-name"
                      value={form.display_name || ""}
                      onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Tagline">
                    <input
                      value={form.tagline || ""}
                      onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Concierge email">
                    <input
                      type="email"
                      value={form.concierge_email || ""}
                      onChange={(e) => setForm({ ...form, concierge_email: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      value={form.phone || ""}
                      onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                      inputMode="tel"
                      placeholder="(555) 123-4567"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Primary color">
                    <ColorInput
                      value={form.primary_color || ""}
                      onChange={(v) => setForm({ ...form, primary_color: v })}
                      testId="admin-clinic-primary"
                    />
                  </Field>
                  <Field label="Accent color">
                    <ColorInput
                      value={form.accent_color || ""}
                      onChange={(v) => setForm({ ...form, accent_color: v })}
                      testId="admin-clinic-accent"
                    />
                  </Field>
                </div>
                <Field label="Logo URL" hint="Paste a static URL (admin override). Clinic can also self-upload from their portal.">
                  <input
                    value={form.logo_url || ""}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    placeholder="/api/static/clinic-logos/..."
                    className={inputCls}
                  />
                </Field>
                {form.logo_url && (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
                      <img src={resolveAssetUrl(form.logo_url)} alt="logo" className="w-full h-full object-cover" />
                    </span>
                    Current logo preview
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={save}
                    data-testid="admin-clinic-branding-save"
                    className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
                  >
                    <Save className="w-3.5 h-3.5" /> Save override
                  </button>
                  <button
                    onClick={cancel}
                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <span className="text-xs text-slate-400 ml-auto">
                    Clinic-set branding remains intact unless overridden here.
                  </span>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
                Select a clinic on the left to view or override its branding.
              </div>
            )}
          </div>
        </div>
      )}
    </ToolPageShell>
  );
}

function ColorInput({ value, onChange, testId }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#0B1733";
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={safe}
        data-testid={`${testId}-picker`}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 rounded-lg border border-slate-200 cursor-pointer bg-white"
      />
      <input
        type="text"
        data-testid={`${testId}-hex`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#0B1733"
        className={`${inputCls} flex-1 font-mono text-xs`}
      />
    </div>
  );
}
